import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import API from "../../config/config";
import { toast } from "react-toastify";
import { compareNames } from "../Driver/compareNames";
import { isValidFullName } from "../../utils/Validator";

const StepPAN = forwardRef(
  ({ formData, setFormData, driver, disabled, initialFormData }, ref) => {
    const [uploading, setUploading] = useState(false);
    const token = localStorage.getItem("adminToken");

    useEffect(() => {
      const dlName =
        driver?.drivingLincenseDetails?.nameToMatch?.trim() ||
        formData?.nameToMatch?.trim();

      if (!formData.holderName && dlName) {
        setFormData((prev) => ({
          ...prev,
          holderName: dlName,
        }));
      }
    }, []);

    const isChanged = () => {
      if (!initialFormData) return true;

      return (
        formData.panNumber !== initialFormData.panNumber ||
        formData.holderName !== initialFormData.holderName ||
        formData.panFile !== initialFormData.panFile
      );
    };

    const handleChange = (field, value) => {
      if (field === "panNumber") {
        const formatted = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
        if (formatted.length <= 10) {
          setFormData((prev) => ({ ...prev, [field]: formatted }));
        }
        return;
      }

      if (field === "holderName") {
        const onlyLetters = value.replace(/[^A-Za-z\s]/g, "");
        setFormData((prev) => ({ ...prev, [field]: onlyLetters }));
        return;
      }

      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const uploadFile = async (file) => {
      const formDataUpload = new FormData();
      formDataUpload.append("documentFor", "Driver");
      formDataUpload.append("type", "pan_front");
      formDataUpload.append("ownerId", driver._id);
      formDataUpload.append("file", file, file.name);

      const response = await fetch(API.ADMIN.ADMINDOCUMENT_UPLOAD, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
        },
        body: formDataUpload,
      });

      if (!response.ok) throw new Error("PAN file upload failed");

      const data = await response.json();
      return data.document?.documentUrl;
    };

    const verifyPAN = async (panFileUrl) => {
      const payload = {
        ownerId: driver._id,
        panNumber: formData.panNumber,
        holderName: formData.holderName,
        consent: "Y",
        consentText:
          "I hereby declare my consent for fetching my information via ZOOP API",
        taskId: "baad2be0-e9c3-4217-9f4f-3c7808f317ba",
        panfront: panFileUrl,
      };

      const response = await fetch(API.ADMIN.ADMIN_VERIFY_PAN_CARD, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || "PAN verification failed");
      }

      return result;
    };

    useImperativeHandle(ref, () => ({
      validateAndUpload: async () => {
        const requiredFields = [
          { field: formData.panNumber, label: "PAN Number" },
          { field: formData.holderName, label: "Holder Name" },
          { field: formData.panFile, label: "PAN Card Image" },
        ];

        for (const { field, label } of requiredFields) {
          if (!field) {
            toast.error(`Please provide ${label}.`);
            throw new Error(`Please provide ${label}.`);
          }
        }

        // Validate name format (only letters and spaces)
        if (!isValidFullName(formData.holderName)) {
          toast.error("Full Name must contain only letters and spaces.");
          throw new Error("Full Name must contain only letters and spaces.");
        }

        // Validate name matching with driving license
        if (
          driver?.drivingLincenseDetails?.nameToMatch &&
          !compareNames(
            formData.holderName,
            driver.drivingLincenseDetails.nameToMatch
          )
        ) {
          toast.error(
            "The name on PAN Card does not match the name on your Driving License. Please enter the correct name."
          );
          throw new Error(
            "The name on PAN Card does not match the name on your Driving License. Please enter the correct name."
          );
        }

        if (!isChanged()) {
          return "unchanged";
        }

        setUploading(true);
        try {
          const panFileUrl =
            typeof formData.panFile === "string"
              ? formData.panFile
              : await uploadFile(formData.panFile);

          await verifyPAN(panFileUrl);

          setFormData((prev) => ({
            ...prev,
            panFile: panFileUrl,
            panNumber: formData.panNumber,
            holderName: formData.holderName,
          }));

          toast.success("PAN submitted and verification initiated");

          return true;
        } finally {
          setUploading(false);
        }
      },
    }));

    return (
      <div className="max-w-4xl mx-auto p-4 bg-white rounded shadow text-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 font-medium text-gray-700 text-sm">
              PAN Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="panNumber"
              value={formData.panNumber || ""}
              onChange={(e) => handleChange("panNumber", e.target.value)}
              placeholder="Enter PAN Number"
              className="w-full border px-2 py-1 rounded text-sm mb-2"
              disabled={uploading || disabled}
              required
            />
          </div>

          <div>
            <label className="block mb-1 font-medium text-gray-700 text-sm">
              Holder Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="holderName"
              value={formData.holderName || ""}
              onChange={(e) => handleChange("holderName", e.target.value)}
              placeholder="Enter Name as per PAN"
              className="w-full border px-2 py-1 rounded text-sm mb-2"
              disabled={uploading || disabled}
              required
            />
          </div>
        </div>

        <div>
          <label className="block mb-1 font-medium text-gray-700">
            Upload PAN File <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            accept="image/*,application/pdf"
            onChange={(e) => handleChange("panFile", e.target.files[0])}
            className="w-full border px-2 py-1 rounded text-sm mb-2"
            required={!formData.panFile}
            disabled={uploading || disabled}
          />

          {formData.panFile && (
            <img
              src={
                typeof formData.panFile === "string"
                  ? formData.panFile
                  : URL.createObjectURL(formData.panFile)
              }
              alt="PAN Preview"
              className="w-24 mt-1 border rounded"
            />
          )}

          {uploading && (
            <div className="text-blue-600 font-semibold mt-2">Uploading...</div>
          )}
        </div>
      </div>
    );
  }
);

export default StepPAN;
